/**
 * 五阶段引导式建包（B1b）API 模块。
 * 端点：POST /guided-build/sessions、advance / confirm / abandon、GET 列表与详情。
 */
import apiClient from './client';

export interface ComponentPlanItem {
  type: 'persona' | 'skill' | 'knowledge' | 'tool' | 'workflow';
  name: string;
  purpose: string;
}

export interface NotCreatingItem {
  type: string;
  name: string;
  reason: string;
}

export interface Recommendation {
  recommendation: string;
  components: ComponentPlanItem[];
  not_creating: NotCreatingItem[];
}

export interface DiscoverView {
  summary: string;
  target_role: string | null;
  components: ComponentPlanItem[];
  not_creating: NotCreatingItem[];
  recommendation: Recommendation | null;
  confirmed: boolean;
}

export interface PlanView {
  components: ComponentPlanItem[];
  not_creating: NotCreatingItem[];
  confirmed: boolean;
}

export interface ClarifyQuestion {
  id: string;
  question: string;
  options: string[];
}

export interface ClarifyGroup {
  title: string;
  skippable: boolean;
  questions: ClarifyQuestion[];
}

export interface ClarifyView {
  question_groups: ClarifyGroup[];
  answers: Record<string, string>;
  skipped_groups: string[];
}

export interface DraftSkill {
  name?: string;
  display_name: string;
  description?: string | null;
  when_to_use?: string | null;
  instructions?: string | null;
  category?: string;
  tags?: string[];
}

export interface Draft {
  name: string;
  description: string;
  domain: string | null;
  persona: {
    identity?: { role?: string; domain?: string; experience?: string };
    communication?: { tone?: string; style?: string };
    [key: string]: unknown;
  };
  skills: DraftSkill[];
  assumptions: string[];
}

export interface ImplementView {
  draft: Draft;
}

export interface ReviewView {
  draft: Draft;
  plan: { components: ComponentPlanItem[]; not_creating: NotCreatingItem[] };
  summary: string;
  created: { agent_id: string; profile_id: string; skill_count: number } | null;
}

export type SessionStage = 'discover' | 'plan' | 'clarify' | 'implement' | 'review';
export type SessionStatus = 'active' | 'completed' | 'abandoned';

export interface GuidedSession {
  id: string;
  stage: SessionStage;
  status: SessionStatus;
  description: string;
  agent_name: string | null;
  agent_domain: string | null;
  agent_description: string | null;
  agent_id: string | null;
  created_at: string;
  updated_at: string;
  view?: DiscoverView | PlanView | ClarifyView | ImplementView | ReviewView;
}

export interface AdvancePayload {
  plan?: { components: ComponentPlanItem[]; not_creating: NotCreatingItem[] } | null;
  confirm_plan?: boolean;
  note?: string | null;
  answers?: Record<string, string> | null;
  skipped_groups?: string[] | null;
  draft_overrides?: Partial<Draft> | null;
}

export interface ConfirmResult {
  session: GuidedSession;
  created: { agent_id: string; profile_id: string; skill_count: number };
  reused: boolean;
}

export async function createGuidedSession(description: string): Promise<GuidedSession> {
  const res = await apiClient.post('/guided-build/sessions', { description });
  return res.data.session as GuidedSession;
}

export async function advanceGuidedSession(id: string, payload: AdvancePayload): Promise<GuidedSession> {
  const res = await apiClient.post(`/guided-build/sessions/${id}/advance`, payload);
  return res.data.session as GuidedSession;
}

export async function confirmGuidedSession(id: string): Promise<ConfirmResult> {
  const res = await apiClient.post(`/guided-build/sessions/${id}/confirm`);
  return res.data as ConfirmResult;
}

export async function listGuidedSessions(status?: SessionStatus): Promise<GuidedSession[]> {
  const res = await apiClient.get('/guided-build/sessions', { params: status ? { status } : {} });
  return res.data as GuidedSession[];
}

export async function getGuidedSession(id: string): Promise<GuidedSession> {
  const res = await apiClient.get(`/guided-build/sessions/${id}`);
  return res.data.session as GuidedSession;
}

export async function abandonGuidedSession(id: string): Promise<GuidedSession> {
  const res = await apiClient.post(`/guided-build/sessions/${id}/abandon`);
  return res.data.session as GuidedSession;
}
